import React, { useCallback, useEffect, useMemo, useState } from "react";
import DateSelect from "../../components/DateSelect.jsx";
import { useDispatch, useSelector } from "react-redux";
import { FaChartLine, FaReceipt, FaPaperPlane, FaBoxesStacked, FaPerson, FaFileInvoiceDollar, FaMoneyBillWave, FaScaleBalanced } from "react-icons/fa6";
import { downloadCSV, downloadMpTaxCalculationCSV, downloadMpAppFeeCSV, downloadDirectAppFeeCSV, downloadRevenueReconciliationXlsx, getPDFAnalytics } from "../../api/index.js";
import {
  DownloadButton,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons.jsx";
import { PDFViewer } from "@react-pdf/renderer";
import SalesReport from "../../partials/salesReport/SalesReport.jsx";
import ModalBasic from "../../components/ModalBasic.jsx";
import DropdownMulti from "../../components/DropdownMulti.jsx";
import CanView from "../../components/CanView.jsx";
import {
  createReportSchedule,
  deleteReportScheduleById,
  fetchReportSchedules,
} from "../../api/settings.js";
import { addToast } from "../../store/app/action.js";

function getFriendlyScheduleString(schedule) {
  if (!schedule) return "";

  const dayMap = {
    0: "Sunday",
    1: "Monday",
    2: "Tuesday",
    3: "Wednesday",
    4: "Thursday",
    5: "Friday",
    6: "Saturday",
  };

  const [frequency, part1, part2] = schedule.split("|");

  switch (frequency) {
    case "daily":
      return "";

    case "weekly": {
      if (!part1) return "Weekly";
      const days = part1
        .split(",")
        .map((d) => dayMap[d.trim()] || "")
        .filter(Boolean);
      return days.join(", ");
    }

    case "monthly": {
      if (part1 === "abs") {
        const suffix = (n) => {
          if (n.endsWith("11") || n.endsWith("12") || n.endsWith("13"))
            return `${n}th`;
          if (n.endsWith("1")) return `${n}st`;
          if (n.endsWith("2")) return `${n}nd`;
          if (n.endsWith("3")) return `${n}rd`;
          return `${n}th`;
        };
        const dates = part2
          .split(",")
          .map((d) => suffix(d.trim()))
          .filter(Boolean);
        return `On the ${dates.join(", ")}`;
      } else {
        const ordinal = part2.charAt(0).toUpperCase() + part2.slice(1);
        const dow = part1.charAt(0).toUpperCase() + part1.slice(1);
        return `${ordinal} ${dow}`;
      }
    }

    default:
      return "";
  }
}

function ReportsPage() {
  const dispatch = useDispatch();
  const { user, selectedMerchant } = useSelector((state) => state.app);
  const [selectedLocations, setSelectedLocations] = useState(["_all"]);
  const [scheduleLocations, setScheduleLocations] = useState([]);
  const [forceFetchSchedules, setForceFetchSchedules] = useState(new Date());

  const [start, setStartDate] = useState(null);
  const [end, setEndDate] = useState(null);
  const [pdfModal, setPDFModal] = useState({});
  const [scheduling, setScheduling] = useState(null);
  const [frequency, setFrequency] = useState("daily");
  const [email, setEmail] = useState(user?.email || "");
  const [subject, setSubject] = useState("");
  const [monthlyMode, setMonthlyMode] = useState("each"); // 'each' or 'on'
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedDays, setSelectedDays] = useState([]);
  const [ordinal, setOrdinal] = useState("first");
  const [dayOfWeek, setDayOfWeek] = useState("day");
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const reports = useMemo(() => {
    const groupedSchedules = schedules.reduce((acc, schedule) => {
      if (!acc[schedule.report]) {
        acc[schedule.report] = [];
      }
      acc[schedule.report].push(schedule);
      return acc;
    }, {});
    let retVal = [
      {
        title: "Sales Snapshot",
        description:
          "A high-level PDF summary of total sales, revenue, and key metrics for the selected time period",
        icon: <FaChartLine />,
        pdf: async () => {
          const data = await getPDFAnalytics(start, end, selectedLocations);
          setPDFModal({ status: "open", data });
        },
        schedules: groupedSchedules["Sales Snapshot"] || [],
      },
      {
        title: "Detailed Order Report",
        description:
          " A comprehensive breakdown of all orders, including delivery details, customer info, fees, and fulfillment status.",
        icon: <FaReceipt />,
        csv: async () => {
          await downloadCSV(start, end, selectedLocations, "past-orders");
        },
        schedules: groupedSchedules["Detailed Order Report"] || [],
      },
      {
        title: "Product Performance Report",
        description:
          "Aggregated product-level data showing units sold and revenue for each item during the selected timeframe.",
        icon: <FaBoxesStacked />,
        csv: async () => {
          await downloadCSV(start, end, selectedLocations, "product-sales");
        },
        schedules: groupedSchedules["Product Performance Report"] || [],
      },
      {
        title: "Customer Summary",
        description: (
          <div>
            A list of customers with names, email, phone, order count, and
            total spend.{" "}
            <span className="text-xs">
              Tip: To include customers who haven’t placed an order, set the
              date range to “All Time”.
            </span>
          </div>
        ),
        icon: <FaPerson />,
        csv: async () => {
          await downloadCSV(start, end, selectedLocations, "customers");
        },
        schedules: groupedSchedules["Customer Summary"] || [],
      },
    ];

    if (scheduling) {
      if (
        scheduling?.schedules.length !==
        retVal.find((report) => report.title === scheduling.title)?.schedules
          .length
      ) {
        setScheduling(
          retVal.find((report) => report.title === scheduling.title)
        );
      }
    }
    return retVal;
  }, [schedules, scheduling, start, end, selectedLocations]);

  useEffect(() => {
    if (selectedMerchant && forceFetchSchedules) {
      fetchReportSchedules().then((res) => {
        setSchedules(res);
      });
    }
  }, [selectedMerchant, forceFetchSchedules]);

  const convertStateToFrequencyString = useCallback(() => {
    switch (frequency) {
      case "daily":
        return "daily";
      case "weekly":
        return "weekly|" + selectedDays.join(","); // e.g., "1,3,5"
      case "monthly":
        if (monthlyMode === "each") {
          return `monthly|abs|${selectedDates.join(",")}`; // e.g., "abs|1,15,30"
        } else {
          return `monthly|${dayOfWeek}|${ordinal}`; // e.g., "monday|first"
        }
      default:
        return "";
    }
  }, [frequency, selectedDays, selectedDates, ordinal, dayOfWeek]);

  const saveSchedule = async () => {
    if (scheduleLocations.length === 0) {
      dispatch(
        addToast({
          type: "error",
          message: `Select at least one location to schedule ${scheduling.title}.`,
          timer: 10,
        })
      );
      return;
    }
    if (subject.trim().length === 0) {
      dispatch(
        addToast({
          type: "error",
          message: `Enter a subject line for ${scheduling.title}.`,
          timer: 10,
        })
      );
      return;
    }
    if (frequency === "monthly" && selectedDates.length === 0) {
      dispatch(
        addToast({
          type: "error",
          message: `Select at least one date to schedule ${scheduling.title}.`,
          timer: 10,
        })
      );
      return;
    }
    if (frequency === "weekly" && selectedDays.length === 0) {
      dispatch(
        addToast({
          type: "error",
          message: `Select at least one day to schedule ${scheduling.title}.`,
          timer: 10,
        })
      );
      return;
    }
    let emails = email.split(",").map((e) => e.trim().toLowerCase());
    let isValidEmail = (email) => /^\S+@\S+\.\S+$/.test(email);
    let invalidEmails = emails.filter((email) => !isValidEmail(email));
    if (invalidEmails.length > 0) {
      dispatch(
        addToast({
          type: "error",
          message: `The following emails are invalid: ${invalidEmails.join(
            ", "
          )}`,
          timer: 10,
        })
      );
      return;
    }
    let uniqueEmails = new Set(emails);

    const schedule = {
      report: scheduling.title,
      email: Array.from(uniqueEmails).join(","),
      frequency: frequency,
      subject: subject,
      schedule: convertStateToFrequencyString(),
      locations: scheduleLocations,
    };
    try {
      setLoading(true);
      await createReportSchedule(schedule);
      setForceFetchSchedules(new Date());
    } catch (e) {
      let message =
        "There was an error processing your request.  Please try again later.";
      try {
        const serverMessage = e.response?.data?.error;
        if (serverMessage) {
          message = serverMessage;
        }
      } catch {}
      dispatch(
        addToast({
          type: "error",
          message,
          timer: 10,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!scheduling) {
      setFrequency("daily");
      setMonthlyMode("each");
      setSubject("");
      setSelectedDates([]);
      setSelectedDays([]);
      setOrdinal("first");
      setDayOfWeek("day");
      setEmail(user?.email);
      setScheduleLocations(selectedLocations);
    }
  }, [scheduling, user]);

  const storeDropdown = useMemo(() => {
    if (!selectedMerchant) return [];
    setSelectedLocations([
      "_all",
      ...selectedMerchant.locations.map((location) => location.id),
    ]);
    return [
      { label: "Aggregate all locations", id: "_all" },
      ...selectedMerchant.locations.map((location) => ({
        label: location.name,
        id: location.id,
      })),
    ];
  }, [selectedMerchant]);

  const [downloadStates, setDownloadStates] = useState({});

  const setReportState = (title, state) => {
    setDownloadStates((prev) => ({
      ...prev,
      [title]: {
        ...prev[title],
        ...state,
      },
    }));
  };
  const handleDownload = useCallback(
    async (report, type) => {
      if (!report.systemWide && selectedLocations.length === 0) {
        dispatch(
          addToast({
            type: "error",
            message: `Select at least one location to download ${report.title}.`,
            timer: 15,
          })
        );
        return;
      }
      if (!start || !end) {
        dispatch(
          addToast({
            type: "error",
            message: `Select a date range to download ${report.title}.`,
            timer: 15,
          })
        );
        return;
      }
      setReportState(report.title, { downloading: true, downloaded: false });
      try {
        switch (type) {
          case "pdf":
            await report.pdf();
            break;
          case "csv":
            await report.csv();
            break;
          case "xlsx":
            await report.xlsx();
            break;
        }
        setReportState(report.title, { downloading: false, downloaded: true });
        setTimeout(() => {
          setReportState(report.title, { downloaded: false });
        }, 3000);
      } catch (e) {
        setReportState(report.title, { downloading: false, downloaded: false });
        dispatch(
          addToast({
            type: "error",
            message: `Failed to download ${report.title}. Please try again.`,
            timer: 15,
          })
        );
      }
    },
    [selectedLocations, start, end, dispatch]
  );

  const adminReports = useMemo(
    () => [
      {
        title: "MP Tax Calculation",
        description:
          "System-wide marketplace tax calculation export for completed non-direct orders in the selected date range.",
        icon: <FaFileInvoiceDollar />,
        systemWide: true,
        csv: async () => {
          await downloadMpTaxCalculationCSV(start, end);
        },
      },
      {
        title: "MP App Fee Report",
        description:
          "System-wide marketplace app fee export for completed non-direct orders, including DSP delivery fees and tips.",
        icon: <FaMoneyBillWave />,
        systemWide: true,
        csv: async () => {
          await downloadMpAppFeeCSV(start, end);
        },
      },
      {
        title: "Direct App Fee Report",
        description:
          "System-wide Direct app fee export for completed Direct orders, including delivery fees, service fees, and payment gateway.",
        icon: <FaMoneyBillWave />,
        systemWide: true,
        csv: async () => {
          await downloadDirectAppFeeCSV(start, end);
        },
      },
      {
        title: "Revenue Reconciliation",
        description:
          "Stripe balance changes vs completed MP and Direct order fees for the selected UTC date range.",
        icon: <FaScaleBalanced />,
        systemWide: true,
        xlsx: async () => {
          await downloadRevenueReconciliationXlsx(start, end);
        },
      },
    ],
    [start, end]
  );

  const renderReportCard = (report, idx, { allowSchedule = true } = {}) => {
    const isScheduled = report.schedules?.length > 0;
    const state = downloadStates[report.title] || {};
    const { downloading, downloaded } = state;

    return (
      <div
        key={idx}
        className="w-full h-44 md:w-96 md:h-48
  bg-white dark:bg-gray-800
  border border-gray-200 dark:border-gray-700
  rounded-2xl shadow-lg dark:shadow-sm
  transition-all duration-200
  flex flex-col justify-between p-4
  text-gray-900 dark:text-gray-100"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/10 rounded-full">
            {report.icon}
          </div>
          <div className="font-semibold text-base">{report.title}</div>
        </div>

        <div className="text-sm text-gray-700 dark:text-gray-300 mt-2 line-clamp-3">
          {report.description}
        </div>

        <div className="flex gap-2 mt-auto">
          {report.csv && (
            <DownloadButton
              type="csv"
              onClick={async () => {
                await handleDownload(report, "csv");
              }}
              loading={downloading}
              success={downloaded}
            />
          )}
          {report.xlsx && (
            <DownloadButton
              type="xlsx"
              onClick={async () => {
                await handleDownload(report, "xlsx");
              }}
              loading={downloading}
              success={downloaded}
            />
          )}
          {report.pdf && (
            <DownloadButton
              type="pdf"
              onClick={async () => {
                await handleDownload(report, "pdf");
              }}
              loading={downloading}
              success={downloaded}
            />
          )}

          {allowSchedule && (
            <button
              onClick={() => {
                setScheduleLocations(selectedLocations);
                setScheduling(report);
              }}
              className={`group relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                isScheduled
                  ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800"
                  : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              <FaPaperPlane className="transition-transform group-hover:scale-110" />
              {isScheduled ? "Scheduled Reports" : "Schedule this Report"}
              {isScheduled && (
                <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-green-600 text-white dark:bg-green-400 dark:text-black">
                  {report.schedules.length}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      {/* Page header */}
      <div className="sm:flex sm:justify-between sm:items-center mb-8">
        {/* Left: Title */}
        <div className="mb-4 sm:mb-0">
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold flex items-center gap-1">
            Reports
          </h1>
        </div>
      </div>
      <div className="mb-8 gap-2 flex flex-row flex-wrap">
        <DateSelect
          onSelect={({ start, end }) => {
            setStartDate(start);
            setEndDate(end);
          }}
        />
      </div>

      <div className="mb-10">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            Merchant Reports
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Scoped to the selected merchant and locations.
          </p>
        </div>
        {selectedMerchant?.locations?.length > 1 && (
          <div className="mb-4 flex items-center relative w-[400px] max-w-full">
            <DropdownMulti
              options={storeDropdown}
              selectedValues={selectedLocations}
              setSelectedValues={setSelectedLocations}
              emptyPlaceholder={"Select location"}
            />
          </div>
        )}
        <div className="flex gap-4 flex-wrap">
          {reports.map((report, idx) => renderReportCard(report, idx))}
        </div>
      </div>

      <CanView permissions="saucey_admin">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            Saucey Admin Reports
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            System-wide reports. Date range only — not scoped to a merchant or store.
          </p>
        </div>
        <div className="flex gap-4 flex-wrap">
          {adminReports.map((report, idx) =>
            renderReportCard(report, `admin-${idx}`, { allowSchedule: false })
          )}
        </div>
      </CanView>
      <ModalBasic
        modalOpen={pdfModal.status === "open"}
        setModalOpen={() => {
          setPDFModal({ status: "ready", data: pdfModal.data });
        }}
        title={"Sales Report"}
      >
        <div className={"w-full h-[1152px]"}>
          {pdfModal.status === "open" && (
            <PDFViewer style={{ width: "100%", height: "100%" }}>
              <SalesReport
                data={pdfModal.data}
                startDate={start}
                endDate={end}
                isAggregate={selectedLocations.includes("_all")}
                selectedLocations={selectedMerchant.locations.filter(
                  (location) => selectedLocations.includes(location.id)
                )}
                selectedMerchant={selectedMerchant}
              />
            </PDFViewer>
          )}
        </div>
      </ModalBasic>
      <ModalBasic
        width="3xl"
        modalOpen={scheduling}
        setModalOpen={() => {
          setScheduling(null);
        }}
        title={scheduling?.title}
      >
        <div className={"text-sm gap-2 flex flex-col"}>
          <h2 className="text-lg font-bold pb-2">
            Create new reporting schedule
          </h2>
          <div className="flex items-start relative w-96 max-w-full flex gap-1 flex-col">
            Locations:
            <DropdownMulti
              options={storeDropdown}
              selectedValues={scheduleLocations}
              setSelectedValues={setScheduleLocations}
              emptyPlaceholder={"Select location"}
            />
          </div>
          <div>
            Email (Comma separated):
            <br />
            <input
              type="text"
              className="form-input w-96"
              placeholder="Enter recipient emails, comma separated"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            Subject:
            <br />
            <input
              type="text"
              className="form-input w-96"
              placeholder="Enter subject line of email"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>
          <div>
            Frequency:
            <br />
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="form-select"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          {frequency === "weekly" && (
            <div className="grid grid-cols-7 gap-2 mt-2 max-w-96">
              {["S", "M", "T", "W", "T", "F", "S"].map((day, i) => (
                <button
                  key={i}
                  onClick={() => {
                    const next = selectedDays.includes(i)
                      ? selectedDays.filter((d) => d !== i)
                      : [...selectedDays, i];
                    setSelectedDays(next);
                  }}
                  className={`text-sm py-1 rounded-md border text-center transition w-10 h-10
          ${
            selectedDays.includes(i)
              ? "bg-indigo-600 text-white"
              : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
          }
        `}
                >
                  {day}
                </button>
              ))}
            </div>
          )}
          {frequency === "monthly" && (
            <div className="mt-2 space-y-4">
              {/* Mode Selection */}
              {/*<div className="flex items-center gap-4">*/}
              {/*  <label className="flex items-center gap-2">*/}
              {/*    <input*/}
              {/*      type="radio"*/}
              {/*      name="monthlyMode"*/}
              {/*      value="each"*/}
              {/*      checked={monthlyMode === "each"}*/}
              {/*      onChange={() => setMonthlyMode("each")}*/}
              {/*    />*/}
              {/*    Each*/}
              {/*  </label>*/}
              {/*  <label className="flex items-center gap-2">*/}
              {/*    <input*/}
              {/*      type="radio"*/}
              {/*      name="monthlyMode"*/}
              {/*      value="on"*/}
              {/*      checked={monthlyMode === "on"}*/}
              {/*      onChange={() => setMonthlyMode("on")}*/}
              {/*    />*/}
              {/*    On the*/}
              {/*  </label>*/}
              {/*</div>*/}

              {/* Date Grid */}
              {monthlyMode === "each" && (
                <div className="grid grid-cols-7 gap-2 max-w-96">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <button
                      key={day}
                      onClick={() =>
                        setSelectedDates((prev) =>
                          prev.includes(day)
                            ? prev.filter((d) => d !== day)
                            : [...prev, day].sort((a, b) => a - b)
                        )
                      }
                      className={`text-sm py-1 rounded-md border text-center transition
              ${
                selectedDates.includes(day)
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              }
            `}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              )}

              {/* Ordinal + Day Picker */}
              {monthlyMode === "on" && (
                <div className="flex items-center gap-2">
                  <select
                    value={ordinal}
                    onChange={(e) => setOrdinal(e.target.value)}
                    className="form-select"
                  >
                    <option value="first">first</option>
                    <option value="second">second</option>
                    <option value="third">third</option>
                    <option value="fourth">fourth</option>
                    <option value="last">last</option>
                  </select>
                  <select
                    value={dayOfWeek}
                    onChange={(e) => setDayOfWeek(e.target.value)}
                    className="form-select"
                  >
                    <option value="day">day</option>
                    <option value="weekday">weekday</option>
                    <option value="weekend">weekend day</option>
                    <option value="sunday">Sunday</option>
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                  </select>
                </div>
              )}
            </div>
          )}
          <div className="mt-4 flex justify-end gap-2">
            <SecondaryButton
              disabled={loading}
              onClick={() => {
                setScheduling(null);
              }}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              loading={loading}
              onClick={async () => {
                await saveSchedule();
              }}
            >
              Create Schedule
            </PrimaryButton>
          </div>

          {/*  dividing line*/}
          {scheduling?.schedules?.length > 0 && (
            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-2">
              <h2 className="text-lg font-bold pb-2">Existing Schedules:</h2>
              <table className="table-auto w-full dark:text-slate-300 text-sm">
                <thead className="text-left border-b border-slate-200 dark:border-slate-700">
                  <tr className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4">Subject</th>
                    <th className="py-2 pr-4">Schedule</th>
                    <th className="py-2 pr-4">Locations</th>
                    <th className="py-2 pr-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduling.schedules.map((s, i) => (
                    <tr
                      key={i}
                      className="border-b border-slate-100 dark:border-slate-800"
                    >
                      <td className="py-2 pr-4">
                        {s.email.split(",").map((e) => (
                          <div>{e}</div>
                        ))}
                      </td>
                      <td className="py-2 pr-4">{s.subject}</td>
                      <td className="py-2 pr-4 capitalize">
                        {s.frequency} {getFriendlyScheduleString(s.schedule)}
                      </td>
                      <td className="py-2 pr-4">
                        {s.sellers?.length === 0
                          ? "All Locations"
                          : s.sellers?.map((l) => (
                              <div>
                                {
                                  selectedMerchant.locations.find(
                                    (loc) => loc.id === l
                                  )?.name
                                }
                              </div>
                            ))}
                      </td>
                      <td className="py-2 pr-4 text-right">
                        <button
                          className="text-xs text-red-600 hover:underline"
                          onClick={() => {
                            deleteReportScheduleById(s._id).then(() => {
                              setForceFetchSchedules(new Date());
                            });
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </ModalBasic>
    </div>
  );
}

export default ReportsPage;
